import { FC } from "react";

interface LinkItem {
  label: string;
  description: string;
  href: string;
}

const RESOURCE_LINKS: LinkItem[] = [
  {
    label: "Developer docs",
    description: "Build on Sugar Rush — one WebSocket, one SDK.",
    href: "/docs",
  },
  {
    label: "API spec",
    description: "The machine-readable AsyncAPI 3.0 contract.",
    href: "/docs/asyncapi.yaml",
  },
  {
    label: "Gummiworm",
    description: "The Cardano Layer 2 that powers Sugar Rush.",
    href: "https://gummiworm.net",
  },
];

const SOCIAL_LINKS: LinkItem[] = [
  {
    label: "X",
    description: "@SugarRushDEX — follow along.",
    href: "https://x.com/SugarRushDEX",
  },
];

const LinkRow: FC<{ item: LinkItem }> = ({ item }) => (
  <a
    href={item.href}
    target={item.href.startsWith("http") ? "_blank" : undefined}
    rel={item.href.startsWith("http") ? "noreferrer" : undefined}
    className="sr-focus-ring group flex items-center justify-between gap-4 rounded-xl border border-sr-border-subtle bg-sr-surface px-4 py-3 transition-colors hover:border-white/10 hover:bg-white/[0.03]"
  >
    <span className="min-w-0">
      <span className="block text-sm font-medium text-white">{item.label}</span>
      <span className="block truncate text-xs text-sr-neutral-500">
        {item.description}
      </span>
    </span>
    <span className="shrink-0 text-sr-neutral-500 transition-colors group-hover:text-sr-lemon-muted">
      →
    </span>
  </a>
);

export const AboutView: FC<{ onShowRules?: () => void }> = ({ onShowRules }) => {
  return (
    <div className="flex w-full max-w-md flex-col gap-6 py-8">
      <div className="flex items-center gap-2.5">
        <h1
          className="text-xl font-semibold text-white"
          style={{ fontFamily: "var(--font-display)" }}
        >
          About
        </h1>
        <img
          src="/images/sugar-rush-logo.svg"
          alt="Sugar Rush"
          className="h-6 w-auto sm:h-7"
        />
      </div>

      {onShowRules && (
        <button
          onClick={onShowRules}
          className="sr-focus-ring group flex items-center justify-between gap-4 rounded-xl border border-sr-border-subtle bg-sr-surface px-4 py-3 text-left transition-colors hover:border-white/10 hover:bg-white/[0.03]"
        >
          <span className="min-w-0">
            <span className="block text-sm font-medium text-white">
              Competition rules
            </span>
            <span className="block truncate text-xs text-sr-neutral-500">
              The official rules of the Sugar Rush Trading Competition.
            </span>
          </span>
          <span className="shrink-0 text-sr-neutral-500 transition-colors group-hover:text-sr-lemon-muted">
            →
          </span>
        </button>
      )}

      <div className="sr-card flex flex-col gap-4 p-5">
        <p className="text-sm leading-relaxed text-sr-oat-milk">
          <span className="font-medium text-white">Sugar Rush</span> is a central
          limit orderbook decentralized exchange (
          <span className="font-medium text-white">CLOB DEX</span>) powered by{" "}
          <span className="font-medium text-white">Gummiworm</span>.
        </p>
        <p className="text-sm leading-relaxed text-sr-oat-milk">
          Currently, we are running a testnet in the form of a trading
          competition. Deposit 10 ADA to receive a basket of testnet tokens and
          compete for{" "}
          <span className="font-medium text-white">$5k in prizes</span>.
        </p>
        <p className="text-sm leading-relaxed text-sr-oat-milk">
          <span className="font-medium text-white">Gummiworm</span> is a Cardano
          Layer 2 designed and built by{" "}
          <span className="font-medium text-white">Sundae Labs</span>.
          Transactions enjoy fast execution via a state channel &ldquo;head&rdquo;,
          but certified in a slower consensus round by a broad and decentralized
          committee of validators.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <h2 className="px-1 text-xs font-medium uppercase tracking-wide text-sr-neutral-500">
          Resources
        </h2>
        {RESOURCE_LINKS.map((item) => (
          <LinkRow key={item.label} item={item} />
        ))}
      </div>

      {SOCIAL_LINKS.length > 0 && (
        <div className="flex flex-col gap-2">
          <h2 className="px-1 text-xs font-medium uppercase tracking-wide text-sr-neutral-500">
            Community
          </h2>
          {SOCIAL_LINKS.map((item) => (
            <LinkRow key={item.label} item={item} />
          ))}
        </div>
      )}

      <p className="px-1 text-xs text-sr-neutral-500">
        Built by{" "}
        <a
          href="https://sundae.fi"
          target="_blank"
          rel="noreferrer"
          className="text-sr-neutral-300 underline decoration-dotted underline-offset-2 transition-colors hover:text-white"
        >
          Sundae Labs
        </a>
        .
      </p>
    </div>
  );
};
